/**
 * The gesture layer for the full-screen viewer.
 *
 * Everything a phone photo viewer is expected to do: drag horizontally and the
 * photo follows your finger, release and it snaps to the next one; pinch with
 * two fingers to zoom; double-tap to zoom to the point you tapped; pan while
 * zoomed; flick down to dismiss.
 *
 * Two decisions shape the implementation:
 *
 *  - **Transforms are written straight to the DOM during a gesture.** Routing
 *    every pointermove through React state would re-render the whole viewer
 *    sixty times a second and the drag would visibly lag the finger. React owns
 *    what is on screen; the gesture owns where it sits until the finger lifts.
 *
 *  - **Intent is detected before the pointer is captured.** Capturing on
 *    pointerdown would swallow taps meant for the video's own controls. Nothing
 *    is stolen until the pointer has actually moved past a threshold, which is
 *    also what stops a slightly-shaky tap from registering as a drag.
 */

import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

const MAX_SCALE = 6;
/** Where a double-tap lands. Roughly iOS: enough to read a face, not a pixel. */
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_MS = 300;
const DOUBLE_TAP_SLOP = 32;
/** Movement before a gesture commits to an axis, in px. */
const INTENT_THRESHOLD = 8;
/** Fraction of the viewport that counts as "far enough" to change photo. */
const SWIPE_DISTANCE_RATIO = 0.22;
/** …or this fast, in px/ms, so a short flick works too. */
const SWIPE_VELOCITY = 0.45;
const DISMISS_DISTANCE = 110;
const SPRING = "transform 260ms cubic-bezier(0.22, 0.61, 0.36, 1)";

type Mode = "undecided" | "swipe" | "pan" | "pinch" | "dismiss" | "none";

interface Point {
  x: number;
  y: number;
}

export interface UseLightboxGesturesOptions {
  index: number;
  count: number;
  /** Gestures are suspended while this is true — e.g. the info panel is open. */
  disabled?: boolean;
  onNavigate: (index: number) => void;
  onClose: () => void;
  /**
   * A tap that was not a drag, not a pinch, and not a double-tap. Receives the
   * event so the caller can tell "tapped the photo" from "tapped beside it".
   */
  onTap?: (event: React.PointerEvent) => void;
  /** Fired when the committed zoom level changes, so the caller can fetch HQ. */
  onZoomChange?: (scale: number) => void;
}

export function useLightboxGestures({
  index,
  count,
  disabled = false,
  onNavigate,
  onClose,
  onTap,
  onZoomChange,
}: UseLightboxGesturesOptions) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const frameRef = useRef<HTMLDivElement | null>(null);

  const pointers = useRef(new Map<number, Point>());
  const mode = useRef<Mode>("none");

  /** Committed transform of the current photo. */
  const view = useRef({ scale: 1, tx: 0, ty: 0 });
  /** Snapshot taken when a gesture begins, so moves are absolute not additive. */
  const start = useRef({
    x: 0,
    y: 0,
    scale: 1,
    tx: 0,
    ty: 0,
    distance: 0,
    midX: 0,
    midY: 0,
    trackX: 0,
  });
  const trackX = useRef(0);
  const velocity = useRef({ x: 0, t: 0, lastX: 0 });
  const lastTap = useRef({ t: 0, x: 0, y: 0 });
  const animating = useRef(false);

  /* ── Writing to the DOM ─────────────────────────────────────────────────── */

  const paintTrack = useCallback((x: number, animate: boolean) => {
    const track = trackRef.current;
    if (!track) return;
    track.style.transition = animate ? SPRING : "none";
    track.style.transform = `translate3d(${String(x)}px, 0, 0)`;
  }, []);

  const paintFrame = useCallback((animate: boolean) => {
    const frame = frameRef.current;
    if (!frame) return;
    const { scale, tx, ty } = view.current;
    frame.style.transition = animate ? SPRING : "none";
    frame.style.transform = `translate3d(${String(tx)}px, ${String(ty)}px, 0) scale(${String(scale)})`;
    frame.dataset.zoomed = scale > 1.01 ? "true" : "";
  }, []);

  /**
   * How far the photo may be dragged before empty space would show.
   *
   * Uses `offsetWidth`, not the bounding rect: the rect already includes the
   * transform we are trying to constrain, which would make the limit drift
   * further out on every move.
   */
  const limits = useCallback(() => {
    const frame = frameRef.current;
    const stage = stageRef.current;
    if (!frame || !stage) return { x: 0, y: 0 };

    /*
     * Measured from the picture, not from the frame around it.
     *
     * The frame now fills the stage — that is what stops a tall photograph
     * overflowing and a small clip shrinking to its poster (see
     * `Lightbox.css`) — so its size says nothing about how much picture there
     * is to pan across. A 4:3 photo letterboxed into a 16:10 stage has empty
     * bars either side, and a limit taken from the frame would happily drag
     * those bars into view.
     *
     * The element is not the picture either: `object-fit: contain` letterboxes
     * *inside* it. So the drawn box is worked out the same way the browser
     * works it out — the natural size scaled to fit — which is the only figure
     * that describes what a person can actually see moving.
     */
    const media = frame.querySelector<HTMLImageElement | HTMLVideoElement>(
      ".fc-lightbox__image, .fc-lightbox__video",
    );

    let contentWidth = frame.offsetWidth;
    let contentHeight = frame.offsetHeight;

    if (media) {
      contentWidth = media.offsetWidth;
      contentHeight = media.offsetHeight;

      const naturalWidth =
        media instanceof HTMLVideoElement
          ? media.videoWidth
          : media.naturalWidth;
      const naturalHeight =
        media instanceof HTMLVideoElement
          ? media.videoHeight
          : media.naturalHeight;

      if (
        naturalWidth > 0 &&
        naturalHeight > 0 &&
        contentWidth > 0 &&
        contentHeight > 0
      ) {
        const fit = Math.min(
          contentWidth / naturalWidth,
          contentHeight / naturalHeight,
        );
        contentWidth = naturalWidth * fit;
        contentHeight = naturalHeight * fit;
      }
    }

    const { scale } = view.current;
    const overflowX = contentWidth * scale - stage.clientWidth;
    const overflowY = contentHeight * scale - stage.clientHeight;
    return { x: Math.max(0, overflowX / 2), y: Math.max(0, overflowY / 2) };
  }, []);

  const clampView = useCallback(() => {
    const bound = limits();
    view.current.tx = Math.max(-bound.x, Math.min(bound.x, view.current.tx));
    view.current.ty = Math.max(-bound.y, Math.min(bound.y, view.current.ty));
  }, [limits]);

  const setScaleAbout = useCallback(
    (nextScale: number, screenX: number, screenY: number) => {
      const stage = stageRef.current;
      if (!stage) return;

      const rect = stage.getBoundingClientRect();
      // Relative to the stage centre, which is what `scale` transforms about.
      const px = screenX - rect.left - rect.width / 2;
      const py = screenY - rect.top - rect.height / 2;

      const { scale, tx, ty } = view.current;
      const clamped = Math.max(1, Math.min(MAX_SCALE, nextScale));

      // Keep whatever is under the fingers under the fingers.
      view.current.scale = clamped;
      view.current.tx = px - ((px - tx) * clamped) / scale;
      view.current.ty = py - ((py - ty) * clamped) / scale;
      clampView();
    },
    [clampView],
  );

  const resetZoom = useCallback(
    (animate = true) => {
      view.current = { scale: 1, tx: 0, ty: 0 };
      paintFrame(animate);
    },
    [paintFrame],
  );

  /* ── Committing a navigation ────────────────────────────────────────────── */

  const settle = useCallback(
    (direction: -1 | 0 | 1) => {
      const stage = stageRef.current;
      const width = stage?.clientWidth ?? window.innerWidth;

      if (direction === 0) {
        trackX.current = 0;
        paintTrack(0, true);
        return;
      }

      animating.current = true;
      const target = -direction * width;
      paintTrack(target, true);

      // transitionend is not guaranteed to fire (an interrupted transition, a
      // backgrounded tab), so the timeout is the one that actually guarantees
      // we are never left mid-swipe with the viewer frozen.
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        animating.current = false;
        trackX.current = 0;
        onNavigate(index + direction);
      };

      trackRef.current?.addEventListener("transitionend", finish, {
        once: true,
      });
      window.setTimeout(finish, 320);
    },
    [index, onNavigate, paintTrack],
  );

  /* ── Pointer handling ───────────────────────────────────────────────────── */

  const midpoint = (): Point & { distance: number } => {
    const [a, b] = [...pointers.current.values()];
    if (!a || !b) return { x: 0, y: 0, distance: 0 };
    return {
      x: (a.x + b.x) / 2,
      y: (a.y + b.y) / 2,
      distance: Math.hypot(a.x - b.x, a.y - b.y),
    };
  };

  const beginPinch = useCallback(() => {
    const { x, y, distance } = midpoint();
    mode.current = "pinch";
    start.current = {
      ...start.current,
      distance,
      midX: x,
      midY: y,
      scale: view.current.scale,
      tx: view.current.tx,
      ty: view.current.ty,
    };
  }, []);

  const onPointerDown = useCallback(
    (event: React.PointerEvent) => {
      if (disabled || animating.current) return;

      pointers.current.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });

      if (pointers.current.size === 2) {
        beginPinch();
        return;
      }
      if (pointers.current.size > 2) return;

      mode.current = "undecided";
      start.current = {
        ...start.current,
        x: event.clientX,
        y: event.clientY,
        scale: view.current.scale,
        tx: view.current.tx,
        ty: view.current.ty,
        trackX: trackX.current,
      };
      velocity.current = { x: 0, t: event.timeStamp, lastX: event.clientX };
    },
    [disabled, beginPinch],
  );

  const onPointerMove = useCallback(
    (event: React.PointerEvent) => {
      if (disabled || pointers.current.size === 0) return;
      if (!pointers.current.has(event.pointerId)) return;

      pointers.current.set(event.pointerId, {
        x: event.clientX,
        y: event.clientY,
      });

      if (mode.current === "pinch") {
        const { x, y, distance } = midpoint();
        if (start.current.distance > 0) {
          const ratio = distance / start.current.distance;
          // Re-derive from the snapshot each move rather than accumulating, so
          // rounding cannot drift the photo away from the fingers.
          view.current.scale = start.current.scale;
          view.current.tx = start.current.tx;
          view.current.ty = start.current.ty;
          setScaleAbout(start.current.scale * ratio, x, y);
          // Two-finger drag moves the photo as well as scaling it.
          view.current.tx += x - start.current.midX;
          view.current.ty += y - start.current.midY;
          clampView();
          paintFrame(false);
        }
        return;
      }

      const dx = event.clientX - start.current.x;
      const dy = event.clientY - start.current.y;

      if (mode.current === "undecided") {
        if (Math.hypot(dx, dy) < INTENT_THRESHOLD) return;

        // Zoomed in, the photo is bigger than the stage, so dragging pans it.
        // Otherwise the axis decides: sideways changes photo, down dismisses.
        if (view.current.scale > 1.01) mode.current = "pan";
        else if (Math.abs(dx) > Math.abs(dy)) mode.current = "swipe";
        else if (dy > 0) mode.current = "dismiss";
        else mode.current = "none";

        if (mode.current === "none") return;
        try {
          (event.target as HTMLElement).setPointerCapture(event.pointerId);
        } catch {
          // Throws when the pointer is no longer active — a lifted finger, or a
          // synthetic event. The gesture still works without capture; it just
          // ends early if the pointer leaves the element.
        }
      }

      const now = event.timeStamp;
      if (now > velocity.current.t) {
        velocity.current = {
          x:
            (event.clientX - velocity.current.lastX) /
            (now - velocity.current.t),
          t: now,
          lastX: event.clientX,
        };
      }

      if (mode.current === "swipe") {
        let next = start.current.trackX + dx;
        // Rubber-band at the ends: the gallery does not wrap, and a hard stop
        // reads as a broken gesture rather than "there is nothing here".
        if ((index === 0 && next > 0) || (index === count - 1 && next < 0)) {
          next = start.current.trackX + dx * 0.32;
        }
        trackX.current = next;
        paintTrack(next, false);
        return;
      }

      if (mode.current === "pan") {
        view.current.tx = start.current.tx + dx;
        view.current.ty = start.current.ty + dy;

        const bound = limits();
        // Panning past the horizontal edge hands the excess to the filmstrip,
        // so a zoomed photo still swipes onward once you reach its edge.
        const overshoot =
          view.current.tx > bound.x
            ? view.current.tx - bound.x
            : view.current.tx < -bound.x
              ? view.current.tx + bound.x
              : 0;

        clampView();
        paintFrame(false);

        if (overshoot !== 0) {
          trackX.current = overshoot;
          paintTrack(overshoot, false);
        }
        return;
      }

      if (mode.current === "dismiss") {
        const progress = Math.min(
          1,
          Math.max(0, dy) / (DISMISS_DISTANCE * 2.5),
        );
        const frame = frameRef.current;
        if (frame) {
          frame.style.transition = "none";
          frame.style.transform = `translate3d(${String(dx * 0.4)}px, ${String(dy)}px, 0) scale(${String(1 - progress * 0.18)})`;
        }
        if (stageRef.current)
          stageRef.current.style.opacity = String(1 - progress * 0.55);
      }
    },
    [
      disabled,
      index,
      count,
      limits,
      clampView,
      paintFrame,
      paintTrack,
      setScaleAbout,
    ],
  );

  const finishGesture = useCallback(
    (event: React.PointerEvent) => {
      pointers.current.delete(event.pointerId);

      if (mode.current === "pinch") {
        // Lifting one finger of two: stay zoomed, but do not treat the leftover
        // finger as the start of a fresh pan halfway across the screen.
        if (pointers.current.size === 1) {
          const [remaining] = [...pointers.current.values()];
          mode.current = "pan";
          start.current = {
            ...start.current,
            x: remaining?.x ?? 0,
            y: remaining?.y ?? 0,
            scale: view.current.scale,
            tx: view.current.tx,
            ty: view.current.ty,
          };
          return;
        }

        if (view.current.scale <= 1.01) resetZoom(true);
        else {
          clampView();
          paintFrame(true);
        }
        onZoomChange?.(view.current.scale);
        mode.current = "none";
        return;
      }

      if (pointers.current.size > 0) return;

      const dx = event.clientX - start.current.x;
      const dy = event.clientY - start.current.y;
      const current = mode.current;
      mode.current = "none";

      if (current === "swipe" || (current === "pan" && trackX.current !== 0)) {
        const width = stageRef.current?.clientWidth ?? window.innerWidth;
        const far = Math.abs(trackX.current) > width * SWIPE_DISTANCE_RATIO;
        const fast = Math.abs(velocity.current.x) > SWIPE_VELOCITY;
        const direction = trackX.current < 0 ? 1 : -1;
        const target = index + direction;

        if ((far || fast) && target >= 0 && target < count) {
          if (current === "pan") resetZoom(false);
          settle(direction as -1 | 1);
        } else {
          settle(0);
        }
        return;
      }

      if (current === "pan") {
        clampView();
        paintFrame(true);
        return;
      }

      if (current === "dismiss") {
        if (dy > DISMISS_DISTANCE) {
          onClose();
        } else {
          if (stageRef.current) stageRef.current.style.opacity = "1";
          paintFrame(true);
        }
        return;
      }

      // Nothing moved: it was a tap.
      if (current === "undecided" && Math.hypot(dx, dy) < INTENT_THRESHOLD) {
        const now = event.timeStamp;
        const isDouble =
          now - lastTap.current.t < DOUBLE_TAP_MS &&
          Math.hypot(
            event.clientX - lastTap.current.x,
            event.clientY - lastTap.current.y,
          ) < DOUBLE_TAP_SLOP;

        if (isDouble) {
          lastTap.current = { t: 0, x: 0, y: 0 };
          if (view.current.scale > 1.01) resetZoom(true);
          else setScaleAbout(DOUBLE_TAP_SCALE, event.clientX, event.clientY);
          paintFrame(true);
          onZoomChange?.(view.current.scale);
          return;
        }

        lastTap.current = { t: now, x: event.clientX, y: event.clientY };
        // Deliberately not delayed by DOUBLE_TAP_MS to disambiguate: toggling
        // the chrome is cheap and reversible, and waiting makes every single
        // tap feel broken.
        onTap?.(event);
      }
    },
    [
      index,
      count,
      clampView,
      paintFrame,
      resetZoom,
      settle,
      setScaleAbout,
      onClose,
      onTap,
      onZoomChange,
    ],
  );

  /* ── Keeping React and the DOM in step ──────────────────────────────────── */

  // Runs before paint, so the incoming photo is never briefly shown at the
  // outgoing photo's offset.
  useLayoutEffect(() => {
    trackX.current = 0;
    view.current = { scale: 1, tx: 0, ty: 0 };
    paintTrack(0, false);
    paintFrame(false);
    if (stageRef.current) stageRef.current.style.opacity = "1";
  }, [index, paintTrack, paintFrame]);

  // A resize changes every limit we derived from the old geometry.
  useEffect(() => {
    const onResize = () => {
      clampView();
      paintFrame(false);
      paintTrack(0, false);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [clampView, paintFrame, paintTrack]);

  const zoomBy = useCallback(
    (factor: number) => {
      const stage = stageRef.current;
      if (!stage) return;
      const rect = stage.getBoundingClientRect();
      setScaleAbout(
        view.current.scale * factor,
        rect.left + rect.width / 2,
        rect.top + rect.height / 2,
      );
      paintFrame(true);
      onZoomChange?.(view.current.scale);
    },
    [setScaleAbout, paintFrame, onZoomChange],
  );

  /** Ctrl+wheel is the desktop pinch; a bare wheel stays a scroll. */
  const onWheel = useCallback(
    (event: React.WheelEvent) => {
      if (disabled || !event.ctrlKey) return;
      event.preventDefault();
      setScaleAbout(
        view.current.scale * (1 - event.deltaY / 300),
        event.clientX,
        event.clientY,
      );
      paintFrame(false);
      onZoomChange?.(view.current.scale);
    },
    [disabled, setScaleAbout, paintFrame, onZoomChange],
  );

  return {
    stageRef,
    trackRef,
    frameRef,
    resetZoom,
    zoomBy,
    handlers: {
      onPointerDown,
      onPointerMove,
      onPointerUp: finishGesture,
      onPointerCancel: finishGesture,
      onWheel,
    },
  };
}
